import { type ReactElement } from "react";
import type { DhanStatus } from "@/lib/dhanApi";

interface Props {
  status: DhanStatus;
  busy: boolean;
  onRenew: () => void;
  onRefresh: () => void;
  onPaste: () => void;
}

const TONE = { warning: "text-warning", danger: "text-danger", muted: "text-ink-muted" } as const;

interface Note {
  tone: keyof typeof TONE;
  text: string;
}

function notesOf(status: DhanStatus): Note[] {
  const notes: Note[] = [];
  if (status.connected && status.derivatives_active === false) {
    notes.push({
      tone: "warning",
      text: "F&O is not active on this Dhan account — price and candle reads work, but option orders would be refused. Activate it on Dhan, then press Refresh.",
    });
  }
  if (status.last_error !== null) notes.push({ tone: "danger", text: status.last_error });
  if (status.connected) {
    notes.push({
      tone: "muted",
      text: status.auto_renew
        ? "The token renews itself every morning at 08:05 IST."
        : "The token lasts 24 hours: log in again each morning, as for Zerodha and Fyers.",
    });
  }
  if (status.connected && status.data_valid_until !== null) {
    notes.push({
      tone: "muted",
      text: `Data plan valid until ${new Date(status.data_valid_until).toLocaleDateString("en-IN")}`,
    });
  }
  return notes;
}

/** What the generic account row cannot say about Dhan: F&O status, the data plan, the last error,
 * how the token is kept alive, and the actions that only make sense for Dhan. */
export function DhanPanel({
  status,
  busy,
  onRenew,
  onRefresh,
  onPaste,
}: Readonly<Props>): ReactElement | null {
  const notes = notesOf(status);
  const canPaste = !status.connected && status.login_enabled;
  if (!status.connected && notes.length === 0 && !canPaste) return null;
  return (
    <div className="mt-2 flex flex-col gap-1.5 text-xs">
      {notes.map((n) => (
        <div key={n.text} className={TONE[n.tone]}>
          {n.text}
        </div>
      ))}
      {status.connected && (
        <PanelActions
          busy={busy}
          showRenew={status.auto_renew}
          onRenew={onRenew}
          onRefresh={onRefresh}
        />
      )}
      {canPaste && (
        <button
          type="button"
          className="self-start text-accent underline"
          onClick={onPaste}
          disabled={busy}
        >
          Paste a token instead
        </button>
      )}
    </div>
  );
}

function PanelActions({
  busy,
  showRenew,
  onRenew,
  onRefresh,
}: Readonly<{
  busy: boolean;
  showRenew: boolean;
  onRenew: () => void;
  onRefresh: () => void;
}>): ReactElement {
  return (
    <div className="flex gap-2">
      {showRenew && (
        <button
          type="button"
          className="btn btn-secondary px-3 py-1 text-xs"
          onClick={onRenew}
          disabled={busy}
        >
          Renew now
        </button>
      )}
      <button
        type="button"
        className="btn btn-secondary px-3 py-1 text-xs"
        onClick={onRefresh}
        disabled={busy}
      >
        Refresh status
      </button>
    </div>
  );
}
